import Tabs from "@/types/ui/Tabs";
import Notification from "@/components/Main/SideBar/Notification.vue"
import Tab from "@/types/ui/Tab";
import Integration from "@/types/organization/Integration";
import { useAuthStore } from "@/stores/AuthStore";
import RowFilters from "@/types/filters/RowFilters";
import User from "@/types/users/User";
import axios from "axios";
import DOMPurify from 'dompurify'
import TopdeskChange from "@/types/topdesk/TopdeskChange";
import { useAPIStore } from "@/stores/APIStore";
import { useUIStore } from "@/stores/UIStore";


/**
 * Calculates bootstrap class based on the window size
 * @param {number} windowWidth the width in pixels of the current window
 * @returns {string} the bootstrap size class associated with this window size
 */
export const calculateWindowSize = (windowWidth: number): string => {
    if (windowWidth >= 1200) {
        return 'lg';
    }
    if (windowWidth >= 992) {
        return 'md';
    }
    if (windowWidth >= 768) {
        return 'sm';
    }
    return 'xs';
};

/**
 * If this function is awaited a thread will sleep for a provided time
 * @param {number} time the number of ms that you want a thread to sleep
 * @returns {Promise<null>} a promise that can be awaited
 */
export const sleep = (time: number): Promise<null> =>
    new Promise((res) => setTimeout(res, time));

/**
 * Sets the classlist of the window
 * @param {string} classList the classList to be added
 */
export const setWindowClass = (classList: string) => {
    const window: HTMLElement | null =
        document && document.getElementById('root');
    if (window) {
        (window as any).classList = classList;
    }
};

/**
 * Adds a class to the window
 * @param {string} classList the classList to be added
 */
export const addWindowClass = (classList: string) => {
    const window: HTMLElement | null =
        document && document.getElementById('root');
    if (window) {
        window.classList.add(classList);
    }
};

export const omit = (obj, key) => {
    const { [key]: _, ...rest } = obj;
    return rest;
}

/**
 * Removes a class from the window
 * @param {string} classList the classList to be added
 */
export const removeWindowClass = (classList: string) => {
    const window: HTMLElement | null =
        document && document.getElementById('root');
    if (window) {
        window.classList.remove(classList);
    }
};

/**
 * Adds a class to the HTML element
 * @param {string} classKey the classKey to be added
 */
export const addRootClass = (classKey: string) => {
    document.getElementsByTagName('html')[0].classList.add(classKey)
}

/**
 * Removes a class from the HTML
 * @param classKey the classKey to be removed
 */
export const removeRootClass = (classKey: string) => {
    document.getElementsByTagName('html')[0].classList.remove(classKey)
}

/**
 * If provided value is a blank string it will be converted to null
 * @param value the value to be processed
 * @returns the resulting value
 */
export const blankToNull = (value: any) => value === "" ? null : value

/**
 * Truncates a string after a given number of characters
 * @param {string} source the string to be truncated
 * @param {number} size the number of characters to truncate
 * @returns {string} the truncated string
 */
export const truncate = (source: string, size: number): string => {
    if (source == null || source == undefined) return null
    try {
        return source.length > size ? source.slice(0, size - 1) + "…" : source;
    } catch (e) {
        return null
    }
}

export const capitalizeFirstLetter = (val) => {
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}

/**
 * Formats a raw sim_status value for display: underscores become spaces and each
 * word is title-cased. E.g. "TEST_READY" or "test_ready" -> "Test Ready".
 * @param status the raw status string
 * @returns the humanised status label
 */
export const formatStatus = (status : string) : string => {
    if (!status) return status
    return status
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase())
}

/**
 * Maps a raw sim_status to the pill label and colour type shown on a SIM card.
 * "active"/"activated" are healthy (green/success), "inactive" or no status is red
 * (error), and any other status (e.g. "TEST_READY") is shown blue (general) with a
 * humanised label so it is not mislabelled as "Inactive".
 * @param status the raw sim_status value
 * @returns the pill label and Pill component type
 */
export const simStatusPill = (status : string) : { label : string, type : string } => {
    const lowercase = status ? status.toLowerCase() : ""
    if (lowercase === "activated" || lowercase === "active") return { label: formatStatus(status), type: "success" }
    if (lowercase === "inactive" || lowercase === "") return { label: "Inactive", type: "error" }
    return { label: formatStatus(status), type: "general" }
}

/**
 * Navigates to the documentation
 * @param document the document to navigate to
 * @param router VueJS router Object
 * @param toast Vue Toastification Object
 * @returns
 */
export const navigateDocumentation = async (document, router, toast, type="location") => {
    console.log(type)
    const base = type == "location" ? "/locations/documentation/" : type == "procedure" ? "/documentation/procedures/" : null
    console.log(base)
    if (base === null) {
        toast(toastError("Unsupported documentation type"));
        return;
    }
    const APIStore = useAPIStore()
    if (document.name.split(".").at(-1) == "pdf")
        router.push(`${base}${document.id}`)
    else {
        const errorMSG = "Something went wrong while fetching this file, please contact the Pollux Administrators"
        try {
            const documentation = type == "location" ? (await APIStore.getLocationDocumentationByID(document.id)) : type == "procedure" ? (await APIStore.getProcedureDocumentationByID(document.id)) : null

            // the documentation does not exist or is not accessible
            if (documentation == null) {
                toast(toastError(errorMSG))
                return
            }

            const url = documentation.documentation
            window.open(url)

        } catch (e) {
            console.error('Failed to open documentation:', e)
            // the location request opts out of the interceptor toast, so the
            // failure is only visible if we report it here
            if (e?.config?.hideToast) toast(toastError(errorMSG))
        }
    }
}

/**
 * Converts a device status to a CSS Bootstrap class
 * @param {string} value the device status as from Meraki
 * @returns the css class associated with this device status
 */
export const getDeviceStatusCSSClass = (value: string) => {
    if (value == null) return "general"
    if (value.toLowerCase() == "online") return "success"
    else if (value.toLowerCase() == "alerting" || value.toLowerCase() == "warning") return "warning"
    else if (value.toLowerCase() == "dormant") return "disabled"
    else if (value.toLowerCase() == "offline" || value.toLowerCase() == "failed") return "error"
    else if (value.toLowerCase() == "no status" || value.toLowerCase() == "unknown" || value.toLowerCase() == "no mx device found") return "general"
}


export const calculateVPNStatus = (networkDevice, downVPNCount) => {
    const dev_status = networkDevice.device_status ? networkDevice.device_status.status : 'dormant'
    if ((dev_status == 'online' || dev_status == 'alerting') && downVPNCount == 0) {
        return 'online'
    } else if ((dev_status == 'online' || dev_status == 'alerting') && downVPNCount == 1) {
        return 'issues'
    } else {
        return 'offline'
    }
}



export const sanitize = (value) => {
    if (value === null) return null
    else if (value === undefined) return undefined
    else if (typeof (value) === "number") return sanitize(String(value))
    else if (typeof (value) === "string") {
        value = value.replaceAll(/[;\"<>\'\&]*/g, "")
        return DOMPurify.sanitize(value)
    } else {
        return null
    }

}


/**
 * Converts tabs to a flattend list of tab names (unsorted)
 * @param {Tabs} tabs 
 * @returns 
 */
export const tabsToListUnsorted = (tabs: Tabs) => Object.values(tabs).map(x => x.name)

/**
 * Converts tabs to a flattend list of tab names (sorted alhpabetically)
 * @param {Tabs} tabs the tabs to be flattened
 * @returns {string[]} the list of tabs as a string list
 */
export const tabsToList = (tabs: Tabs): string[] => Object.values(tabs).map(x => x.name).sort((a, b) => {
    if (a.toLowerCase() == "all") return -1
    if (b.toLowerCase() == "all") return 1
    return (a < b) ? -1 : (a > b) ? 1 : 0
})

/**
 * Sorts all indices of tabs in the same way as the names of the tabs
 * can be used to map a tab index to the correct tab when the keys are listed alphabetically
 * @param {Tabs} tabs the tabs to be converted
 * @returns {string[]} the list of tab keys
 */
export const tabKeysToList = (tabs: Tabs): string[] => Object.keys(tabs).map(x => [x, tabs[x]]).sort((a: any, b: any) => {
    if (a[1].name.toLowerCase() == "all") return -1
    if (b[1].name.toLowerCase() == "all") return 1
    return (a[1].name < b[1].name) ? -1 : (a[1].name > b[1].name) ? 1 : 0
}).map(x => x[0]) as string[]

/**
 * Converts a user to a string
 * @param {User} user the user to be converted
 * @returns {string} the user in string format
 */
export const userToName = (user: User): string => {
    let name = ''
    if (user.first_name && user.last_name) {
        name = `${user.first_name} ${user.last_name}`
    } else if (user.first_name || user.last_name) {
        name = user.first_name || user.last_name
    } else {
        name = 'An Unnamed Colleague'
    }
    return name
}

/**
 * Converts a place object to an address string
 * @param place the geocode place object
 * @returns {string} the address as a string
 */
export const placeToAddress = (place: any): string => {
    const data = []
    if (place.street_name) {

        if (place.house_number) {
            data.push(`${place.street_name} ${place.house_number}`)
        } else {
            data.push(place.street_name)
        }
    }
    if (place.postal_code) data.push(place.postal_code)
    if (place.city) data.push(place.city)
    if (place.country) data.push(place.country)
    return data.join(", ")
}

/**
 * Formats hours and minutes to a string containing both
 * adds padded zeros eg. {hours: 9, minutes: 41} => "09:41"
 * @param hours the hours of a time object
 * @param minutes the minutes of a time object
 * @returns {string} the address as a string
 */
export const formatHoursMinutes = (hours: any, minutes: any) => {
    hours = parseInt(hours)
    minutes = parseInt(minutes)
    const hourStr = hours < 10 ? `0${hours}` : `${hours}`
    const minStr = minutes < 10 ? `0${minutes}` : `${minutes}`
    const time = `${hourStr}:${minStr}`
    return time
}

/**
 * Formats hours, minutes and seconds to a string containing all three
 * adds padded zeros eg. {hours: 9, minutes: 41, seconds: 5} => "09:41:05"
 * @param hours the hours of a time object
 * @param minutes the minutes of a time object
 * @param seconds the seconds of a time object
 * @returns {string} the formatted time string
 */
export const formatHoursMinutesSeconds = (hours: any, minutes: any, seconds: any) => {
    hours = parseInt(hours)
    minutes = parseInt(minutes)
    seconds = parseInt(seconds)
    const hourStr = hours < 10 ? `0${hours}` : `${hours}`
    const minStr = minutes < 10 ? `0${minutes}` : `${minutes}`
    const secStr = seconds < 10 ? `0${seconds}` : `${seconds}`
    return `${hourStr}:${minStr}:${secStr}`
}

/**
 * Formats a date to a postgres proof date string: YYYY-MM-DD
 * @param {Date} date the date to be formatted
 * @returns {string} a date string (yyyy mm dd)
 */
export const formatDate = (date: Date): string =>
    date.getFullYear() + "-" +
    ("0" + (date.getMonth() + 1)).slice(-2) +
    "-" + ("0" + date.getDate()).slice(-2)

/**
 * Formats a date to a date string: DD-MM-YYYY
 * @param {Date} date the date to be formatted
 * @returns {string} a date string (dd mm yyyy)
 */
export const formatDateEU = (date: Date): string =>
    ("0" + date.getDate()).slice(-2) + "-" +
    ("0" + (date.getMonth() + 1)).slice(-2) + "-" +
    date.getFullYear()

/**
 * Formats a date to a readable string: 14 may
 * @param {Date} date the date to be formatted
 * @returns {string} a date string (dd mmm)
 */
export const formatWrittenDate = (date: Date): string => {
    const day = date.getDate()
    const month = date.toLocaleString('default', { month: 'long' })
    return `${day} ${month}`
}

/**
 * Formats a date to a fully readable string: 14 may, 2024
 * @param {Date} date the date to be formatted
 * @returns {string} a date string (dd mmm, yyyy)
 */
export const formatFullWrittenDate = (date: Date): string => {
    const day = date.getDate()
    const month = date.toLocaleString('default', { month: 'long' })
    return `${day} ${month}, ${date.getFullYear()}`
}

/**
 * Converts a date object to a time string with padded zeros: 09:41
 * @param {Date} date the date to be formatted
 * @returns {string} a time string (hh:mm)
 */
export const convertDateToTime = (date: Date): string => {
    return formatHoursMinutes(date.getHours(), date.getMinutes())
}

/**
 * Formats a date to a fully readable datetime string: 14 may, 2024 at 09:41
 * @param {Date} date the date to be formatted
 * @returns {string} a fully formatted date string (dd mmm, yyyy at hh:mm)
 */
export const formatFullDateTime = (date: Date): string => {
    const day = date.getDate()
    const month = date.toLocaleString('default', { month: 'long' })
    return `${day} ${month}, ${date.getFullYear()} at ${formatHoursMinutes(date.getHours(), date.getMinutes())}`
}

/**
 * Formats a date to a full date time stamp
 * @param {Date} date the date to be formatted
 * @returns {string} a fully formatted date string (dd-mm-yyyy_hh:mm:ss)
 */
export const formatFullDateTimeStamp = (date: Date): string => {
    return `${formatDateEU(date)}_${date.getHours()}${date.getMinutes()}${date.getSeconds()}`
}

/**
 * Sorts the changes by date
 * @param {Change[]} changes the changes to be sorted
 * @returns a sorted list of changes
 */
export const sortByReverseCreationDate = (changes: any[], attribute = "created_date") => changes.sort((a, b) => new Date(b[attribute]).getTime() - new Date(a[attribute]).getTime())

/**
 * Formats a date to a fully readable relative datetime string
 *  - if not tomorrow, today or yesterday: 14 may, 2024 at 09:41
 *  - if today: Today at hh:mm
 *  - if yesterday: Yesterday at hh:mm
 *  - if tomorrow: Tomorrow at hh:mm
 * @param {Date} date the date to be formatted
 * @returns {string} a fully formatted date string
 */
export const formatRelativeFullDateTime = (date: Date): string => {
    // today
    var newDate = new Date();
    const today = newDate.toDateString()
    if (date.toDateString() === today) return `Today at ${formatHoursMinutes(date.getHours(), date.getMinutes())}`

    // yesterday
    newDate.setDate(newDate.getDate() - 1);
    const yesterday = newDate.toDateString()
    if (date.toDateString() === yesterday) return `Yesterday at ${formatHoursMinutes(date.getHours(), date.getMinutes())}`

    // tomorrow
    newDate.setDate(newDate.getDate() + 2);
    const tomorrow = newDate.toDateString()
    if (date.toDateString() === tomorrow) return `Tomorrow at ${formatHoursMinutes(date.getHours(), date.getMinutes())}`

    return formatFullDateTime(date)
}

/**
 * Calculates the difference between two dates
 * @param {Date} dateA the more recent date
 * @param {Date} dateB the older date
 * @returns the number of days, hours, minutes and seconds between two dates
 *          or a diffrence of 0 (everything) if dateB is bigger than dateA
 */
export const dateDifference = (dateA: Date, dateB: Date) => {
    if ((dateA as any) - (dateB as any) < 0) {
        return {
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0,
        }
    }

    var delta = Math.abs((dateA as any) - (dateB as any)) / 1000;

    // calculate (and subtract) whole days
    var days = Math.floor(delta / 86400)
    delta -= days * 86400

    // calculate (and subtract) whole hours
    var hours = Math.floor(delta / 3600) % 24
    delta -= hours * 3600;

    // calculate (and subtract) whole minutes
    var minutes = Math.floor(delta / 60) % 60
    delta -= minutes * 60

    // what's left is seconds
    var seconds = Math.floor(delta) % 60

    return {
        days,
        hours,
        minutes,
        seconds,
    }
}

/**
 * Provides a random int
 * @param {number} min the min of the random int range (included)
 * @param {number} max the max of the random int range (included)
 * @returns 
 */
export const random = (min: number, max: number) => {
    return (min + (Math.random() * (max - min + 1))) << 0
}

/**
 * Converts a TopDesk status to a CSS Pill Class
 * @param {string} status a topdesk ticket status
 * @returns {string | null} the css class of the associated pill
 */
export const statusToPillClass = (status: string): string => {
    if (status.toLowerCase() == "completed") return "success"
    if (status.toLowerCase() == "waiting") return "warning"
    if (status.toLowerCase() == "updated") return "warning"
    if (status.toLowerCase() == "in progress") return "general"
    if (status.toLowerCase() == "logged") return "general"
}

/**
 * Converts a TopDesk priority to a CSS Pill Class
 * @param {string} prio the priority of a topdesk
 * @returns {string | null} the css class of the associated pill
 */
export const priorityToPillClass = (prio) => {
    if (prio == "P1") return "error"
    if (prio == "P2") return "warning"
    if (prio == "P3") return "general"
}

/**
 * Safely checks if a value includes a search query
 * @param {string | null} val the value that needs to be checked against the search query
 * @param {string} searchQuery the search query to be matched against
 * @returns {boolean} whether or not the value is includes in the string
 */
export const safeIncludes = (val: string | null, searchQuery: string): boolean => {
    return val != null && val.toLowerCase().includes(searchQuery.toLowerCase())
}

/**
 * Limits a list by a number of elements and join them to a string
 * @param {any[]} list a list of objects to be limited
 * @param {number} limit the number of objects to display
 * @param {string} separator the seperator for the string eg. ', '
 * @returns {string} a, b, c +50 more...
 */
export const limitList = (list: any[], limit: number, separator: string = ", "): string => {
    const shortList = list.slice(0, limit).join(separator)
    const suffix = list.length <= limit ? "" : `+${list.length - limit}...`
    return `${shortList} ${suffix}`
}

/**
 * Converts a file to a base64 format
 * @param file the file object to be converted
 * @returns a promise that resolves with a BASE64 string of the file
 */
export const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });
}

/**
 * If this function is awaited a thread will sleep for a provided time
 * @param {number} ms the number of ms that you want a thread to sleep
 * @returns {Promise<null>} a promise that can be awaited
 */
export const timeout = (ms) => {
    return new Promise(resolve => setTimeout(resolve, ms));
}


export const isValidRedirect = (url) => {
    // Define an array of allowed domains/paths
    const allowedDomains = [
        'pollux.net',
        'veducon.local',
    ];


    try {
        const parsedUrl = new URL(url);
        // Check if the hostname matches any allowed domain
        return allowedDomains.some(domain =>
            parsedUrl.hostname === domain ||
            parsedUrl.hostname.endsWith(`.${domain}`)
        );
    } catch (error) {
        // Invalid URL format
        return false;
    }
}

export const safeRedirect = (url) => {
    url = window.location.origin + "/" + url

    if (isValidRedirect(url)) {
        window.open(url, '_self');
    } else {
        console.error('Unauthorized redirect attempt');
    }
}


/**
 * Default toast settings for more beautiful toasts
 * @param {string} type the toast type, eg. error, success, etc.
 * @param {string} message the message to be displayed
 * @returns a toast component with the desired settings
 */
const toastSettings = (type: string, message: string, options: any = null) => ({
    component: Notification,
    props: {
        message: {
            message: message,
            message_type: type,
            more_info_text: options?.more_info_text,
            more_info_url: options?.more_info_url,
        },
        inToast: true,
    },
})

/**
 * Gives settings for a success toast
 * @param {string} message the message to be displayed
 * @returns toast settings
 */
export const toastSuccess = (message: string, options: any = null) => toastSettings("success", message, options)

/**
 * Gives settings for an error toast
 * @param {string} message the message to be displayed
 * @returns toast settings
 */
export const toastError = (message: string, options: any = null) => toastSettings("error", message, options)

/**
 * Reads a displayable message out of a failed API response.
 *
 * The backend uses two error shapes on the same endpoints: view-level checks answer with
 * `{"errors": [...]}`, while DRF exceptions — including the Meraki region errors, a 503 when
 * the region has no credentials and a 404 when the serial is unknown locally — answer with
 * `{"detail": "..."}`. Reading only one of the two either drops the message or throws inside
 * the error handler.
 *
 * @param {any} e the rejected axios error
 * @param {string} fallback the message to show when the response carries neither shape
 * @returns {string} the message to display
 */
export const apiErrorMessage = (e: any, fallback: string): string => {
    const data = e?.response?.data
    if (data?.detail) return data.detail
    if (Array.isArray(data?.errors) && data.errors.length > 0) return data.errors[0]
    return fallback
}

/**
 * Gives settings for a warning toast
 * @param {string} message the message to be displayed
 * @returns toast settings
 */
export const toastWarning = (message: string, options: any = null) => toastSettings("warning", message, options)

/**
 * Gives settings for a general toast
 * @param {string} message the message to be displayed
 * @returns toast settings
 */
export const toastGeneral = (message: string, options: any = null) => toastSettings("general", message, options)

/**
 * Concats an array with commas and ampersand
 * @param {any[]} arr the list to be concatenated 
 * @returns {string} the concatenated string
 */
export const arrayToString = (arr : any[], final = '&') : string => {
    if(arr == null) return ""
    if(arr.length == 0) return ""

    const finalEl = arr.at(-1);
    return arr.length > 1
    ? arr.slice(0,-1).join(', ') + ` ${final} ` + finalEl
    : finalEl;
}

/**
 * This regex filters urls from plain text messages and returns text with interactable anchor tags
 * @param {string} message the message from which to deduce urls
 * @returns {string} the new message with html anchor tags inserted on the places of urls
 */
export const processUrls = (message: string): string => {
    var text = message
    var exp = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/ig
    var text1 = text.replace(exp, "<a href='$1'>$1</a>")
    var exp2 = /(^|[^\/])(www\.[\S]+(\b|$))/gim
    return text1.replace(exp2, '$1<a target="_blank" href="http://$2">$2</a>')
}

/**
 * Converts a hh:mm:ss string into a time object
 * @param {string} str the time string 
 * @returns the time object
 */
export const timeStringToTime = (str: string) => {
    if (str == null) return null
    const strs = str.split(":")
    return {
        hours: strs[0],
        minutes: strs[1],
        seconds: strs[2],
    }
}

/**
 * Returns a default pagination, 0 results
 * @returns the default pagination response
 */
export const defaultPagination = () => {
    return {
        count: 0,
        next: null,
        previous: null,
        results: []
    }
}

/**
 * Gets the connection status of an integration
 * @param {Integration} integration the integration of which to collect the connection status 
 * @returns {string} the connection status of an integration
 */
export const getIntegrationStatus = (integration: Integration): string => {
    if (integration.organization_settings.length == 0) return "disabled"

    for (const setting of integration.organization_settings) {
        if (!setting.has_value) return "disconnected"
    }
    return "connected"
}

/**
 * Signs a request body with an elevated priviliges token
 * @param {object} rb the request body to be signed
 * @param toast a toast object for showing error messages
 * @returns a signed dictionary
 */
export const signDictWithElevatedPermissions = async (rb: object, toast) => {
    const AuthStore = useAuthStore()
    return await AuthStore.signWithElevatedSession(rb)
}

/**
 * Creates a request body with only an elevated permissions token
 * @param toast a toast object for showing error messages
 * @returns an otherwise empty signed dictionary
 */
export const signWithElevatedPermissions = async (toast) => {
    const AuthStore = useAuthStore()
    return await AuthStore.signWithElevatedSession()
}

/**
 * Gets the response errors of an axios error object
 * @param e the axios error object
 * @returns the response errors or null
 */
export const getResponseErrors = (e) => {
    if (e && e.response && e.response.data && Array.isArray(e.response.data)) return e.response.data
    else return null
}

export const downloadFile = (data, format, module) => {
    const href = URL.createObjectURL(data);

    // create "a" HTML element with href to file & click
    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', `export-${module}-${formatFullDateTimeStamp(new Date())}.${format}`); //or any other extension
    document.body.appendChild(link);
    link.click();

    // clean up "a" element & remove ObjectURL
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
}

export const getOrFile = async (url, { page = 1, pageSize = 1, search = "", filters = {} } = {}, options = {}) => {
    const isFile = filters['format'] != null

    const res = await axios.get(url, {
        params: {
            p: page,
            page_size: pageSize,
            search: search,
            ordering: "-created_at",
            ...filters,
        },
        responseType: isFile ? 'blob' : null,
        ...options
    })

    if (isFile) {
        downloadFile(res.data, filters['format'], url.replaceAll('/', ' ').trim().replace(' ', '-'))
    }

    return res
}

/**
 * Renders a speedscore pill based on speedtest data
 * @param {any} speedtestData - The speedtest data object containing uplink_capacity and downlink_capacity
 * @param {string} maxWidth - The max-width CSS value for the rendered pill
 * @returns {string} - HTML string for the speedscore pill or "No data" if no data is provided
 */
export const renderSpeedscore = (speedtestData: any, maxWidth = "75%"): string => {
    if (!speedtestData) return "No data"

    const upload = Math.round(speedtestData.uplink_capacity * 10) / 10
    const download = Math.round(speedtestData.downlink_capacity * 10) / 10

    const speedtestStatus = upload >= 20 && download >= 20 ? 'success' : 'warning'
    const speedtestLabel = upload >= 20 && download >= 20 ? 'Good' : 'Warning'

    const speedtest = {
        "id": sanitize(speedtestData.id),
        "hostname": sanitize(speedtestData.hostname),
        "uplink_capacity": sanitize(speedtestData.uplink_capacity),
        "downlink_capacity": sanitize(speedtestData.downlink_capacity),
        "uplink_responsiveness": sanitize(speedtestData.uplink_responsiveness),
        "download_responsiveness": sanitize(speedtestData.download_responsiveness),
        "idle_latency": sanitize(speedtestData.idle_latency),
        "timestamp": sanitize(speedtestData.timestamp)
    }

    return `<div
        class="pill speedtest-more-info ${sanitize(speedtestStatus)}" style="max-width: ${sanitize(maxWidth)} !important; width: auto !important;"
        onmouseenter="window.speedometerMouseEnterEvent(event, '${encodeURIComponent(JSON.stringify(speedtest))}')" 
        onmouseleave="window.speedometerMouseLeaveEvent()" 
    > 
        ${sanitize(speedtestLabel)} 
        <img class="speedscore-tooltip-icon" src="/assets/img/speedtest/details-${sanitize(speedtestStatus)}.svg"/>
    </div>`
}


export const checkVersion = async (logout = true) => {
    const apiStore = useAPIStore()
    const authStore = useAuthStore()

    const currentVersion = localStorage.getItem('currentVersion')
    const newCurrentVersion = await apiStore.getCurrentVersionNumber()

    if (currentVersion && currentVersion != newCurrentVersion) {
        localStorage.setItem('currentVersion', newCurrentVersion)

        const isSignUpPage = window.location.pathname.includes('/auth/sign-up')
        if (logout && !isSignUpPage) await authStore.logout(true, true)
    }
}
